"use client"

import { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Search, X, ChefHat, Plus } from "lucide-react"
import { useApp } from "@/lib/context"
import { effectivePrice, type Dish } from "@/lib/api"
import { formatPrice } from "@/lib/utils"
import DishDetailModal from "./dish-detail-modal"

interface GlassSearchProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Pesquisa em glassmorfismo líquido — cobre o ecrã inteiro.
 * Procura pratos, bebidas e o bufete anunciado.
 */
export default function GlassSearch({ isOpen, onClose }: GlassSearchProps) {
  const { t, categories, categoriesBebidas, buffete, addToDraft } = useApp()
  const [termo, setTermo] = useState("")
  const [selectedDish, setSelectedDish] = useState<Dish | null>(null)

  // Foco automático ao abrir + ESC fecha
  useEffect(() => {
    if (!isOpen) return
    setTermo("")
    const t = setTimeout(() => document.getElementById("glass-search-input")?.focus(), 120)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => {
      clearTimeout(t)
      window.removeEventListener("keydown", onKey)
    }
  }, [isOpen, onClose])

  const resultados = useMemo(() => {
    const q = termo.trim().toLowerCase()
    if (!q) return []

    const pratos: Array<{ dish: Dish }> = []
    for (const categoria of [...categories, ...categoriesBebidas]) {
      for (const dish of categoria.pratos) {
        if (
          dish.nome.toLowerCase().includes(q) ||
          dish.descricao?.toLowerCase().includes(q)
        ) {
          pratos.push({ dish })
        }
      }
    }
    return pratos.slice(0, 20)
  }, [termo, categories, categoriesBebidas])

  const buffeteMatch =
    termo.trim().length > 1 &&
    buffete &&
    (buffete.nome.toLowerCase().includes(termo.trim().toLowerCase()) ||
      "bufete".includes(termo.trim().toLowerCase()) ||
      "buffet".includes(termo.trim().toLowerCase()))

  const abrirSecao = (id: string) => {
    onClose()
    setTimeout(
      () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }),
      200,
    )
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[65] bg-background/60 backdrop-blur-2xl"
            onClick={onClose}
          >
            <motion.div
              initial={{ y: -24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -24, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 280 }}
              className="mx-auto flex h-full w-full max-w-xl flex-col px-4 pt-6 safe-top"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Campo de pesquisa liquid-glass */}
              <div className="glass-strong liquid-sheen flex items-center gap-3 rounded-full px-5 py-3.5 shadow-2xl">
                <Search className="size-5 shrink-0 text-primary" aria-hidden="true" />
                <input
                  id="glass-search-input"
                  type="search"
                  value={termo}
                  onChange={(e) => setTermo(e.target.value)}
                  placeholder="Procurar pratos, bebidas ou bufete..."
                  className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
                />
                <button
                  onClick={onClose}
                  className="rounded-full bg-white/40 p-1.5 transition-colors hover:bg-white/60"
                  aria-label="Fechar pesquisa"
                >
                  <X className="size-3.5" />
                </button>
              </div>

              {/* Resultados */}
              <div className="mt-4 flex-1 space-y-2 overflow-y-auto pb-8">
                {/* Atalho do bufete */}
                {buffeteMatch && buffete && (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => abrirSecao("sec-bufete")}
                    className="flex w-full items-center gap-3 rounded-2xl p-4 text-left text-white shadow-lg backdrop-blur transition-transform hover:scale-[1.01]"
                    style={{
                      background:
                        "linear-gradient(120deg, hsl(var(--buffet)), hsl(var(--buffet) / 0.85))",
                    }}
                  >
                    <ChefHat className="size-5 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-serif">{buffete.nome}</span>
                      <span className="block text-xs opacity-80">
                        Bufete · {buffete.quantidadePratos} pratos · {formatPrice(buffete.preco)}
                      </span>
                    </span>
                    <span className="rounded-full bg-white/20 px-3 py-1 text-xs">Ver</span>
                  </motion.button>
                )}

                {resultados.map(({ dish }, i) => (
                  <motion.div
                    key={dish._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.2) }}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedDish(dish)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        setSelectedDish(dish)
                      }
                    }}
                    className="group flex w-full cursor-pointer items-center gap-3 rounded-2xl border border-border/70 bg-card p-3 text-left shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/50"
                  >
                    <div className="size-12 shrink-0 overflow-hidden rounded-xl bg-secondary">
                      {dish.urls?.[0] && !dish.urls[0].includes("placeholder") ? (
                        <img src={dish.urls[0]} alt="" className="size-full object-cover" />
                      ) : (
                        <div className="flex size-full items-center justify-center text-muted-foreground/40">
                          <ChefHat className="size-4" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium group-hover:text-primary">{dish.nome}</p>
                      {dish.descricao && (
                        <p className="truncate text-xs text-muted-foreground">{dish.descricao}</p>
                      )}
                    </div>
                    <span className="shrink-0 font-serif text-base text-primary">
                      {formatPrice(effectivePrice(dish))}
                    </span>
                    <button
                      type="button"
                      aria-label={`${t("addToDraft")}: ${dish.nome}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        addToDraft(dish)
                      }}
                      className="flex size-9 shrink-0 items-center justify-center rounded-full text-white shadow transition-transform hover:scale-105 active:scale-95"
                      style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.85))" }}
                    >
                      <Plus className="size-4" aria-hidden="true" />
                    </button>
                  </motion.div>
                ))}

                {/* Estado vazio */}
                {termo.trim() && resultados.length === 0 && !buffeteMatch && (
                  <div className="pt-16 text-center">
                    <p className="font-serif text-lg font-light text-foreground/70">Nada encontrado</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Tenta outro nome — por exemplo &ldquo;frango&rdquo; ou &ldquo;suco&rdquo;.
                    </p>
                  </div>
                )}

                {!termo.trim() && (
                  <div className="pt-16 text-center">
                    <p className="text-sm text-muted-foreground">
                      Escreve para procurar em todo o menu — pratos, bebidas e bufete.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {selectedDish && (
        <DishDetailModal
          dish={selectedDish}
          zClassName="z-[75]"
          onClose={() => setTimeout(() => setSelectedDish(null), 300)}
        />
      )}
    </>
  )
}
